import { supabase } from "../../supabase/supabase";
import { CallRequest, CallRequestFormData } from "../models/CallRequest";

export const callRequestService = {
  async getCallRequests(organizationId: string): Promise<CallRequest[]> {
    const { data, error } = await supabase
      .from("call_requests")
      .select("*")
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching call requests:", error);
      throw error;
    }

    return data || [];
  },

  async getCallRequestById(id: string): Promise<CallRequest | null> {
    const { data, error } = await supabase
      .from("call_requests")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error(`Error fetching call request with id ${id}:`, error);
      throw error;
    }

    return data;
  },

  async createCallRequest(
    formData: CallRequestFormData,
    organizationId: string,
    userId: string,
  ): Promise<CallRequest> {
    const { data, error } = await supabase
      .from("call_requests")
      .insert([
        {
          ...formData,
          organization_id: organizationId,
          created_by: userId,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error("Error creating call request:", error);
      throw error;
    }

    return data;
  },

  async updateCallRequest(
    id: string,
    formData: Partial<CallRequestFormData>,
  ): Promise<CallRequest> {
    const { data, error } = await supabase
      .from("call_requests")
      .update({
        ...formData,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating call request with id ${id}:`, error);
      throw error;
    }

    return data;
  },

  async deleteCallRequest(id: string): Promise<void> {
    const { error } = await supabase
      .from("call_requests")
      .delete()
      .eq("id", id);

    if (error) {
      console.error(`Error deleting call request with id ${id}:`, error);
      throw error;
    }
  },

  // Scheduling functions have been moved to leadService.ts

  async getAssistantVariables(assistantId: string): Promise<string[]> {
    // This is a placeholder function that would typically fetch the variables
    // used by a specific assistant from the database or analyze the assistant's configuration
    // For now, we'll return a mock list of variables
    const { data, error } = await supabase
      .from("assistants")
      .select("configuration, llm_configuration")
      .eq("id", assistantId)
      .single();

    if (error) {
      console.error(
        `Error fetching assistant variables for id ${assistantId}:`,
        error,
      );
      return [];
    }

    // Extract variables from the assistant's configuration
    // This is a simplified implementation and should be adapted based on
    // how variables are actually stored in your assistant configurations
    const variables: string[] = [];

    // Check for variables in system prompt
    const systemPrompt = data?.llm_configuration?.systemPrompt || "";
    const systemPromptVariables = extractVariablesFromText(systemPrompt);
    variables.push(...systemPromptVariables);

    // Check for variables in first message
    const firstMessage = data?.first_message || "";
    const firstMessageVariables = extractVariablesFromText(firstMessage);
    variables.push(...firstMessageVariables);

    // Check for variables in configuration
    if (data?.configuration) {
      // Add logic to extract variables from other configuration fields as needed
    }

    // Return unique variables
    return [...new Set(variables)];
  },
};

// Helper function to extract variables from text
// This is a simple implementation that looks for patterns like {{variable_name}}
function extractVariablesFromText(text: string): string[] {
  const variableRegex = /\{\{([^}]+)\}\}/g;
  const matches = text.match(variableRegex) || [];
  return matches.map((match) => match.slice(2, -2).trim());
}
